import React, { useEffect, useState, useMemo } from "react";
import { api, apiErrorMessage, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X, DownloadSimple } from "@phosphor-icons/react";

const STATUS_STYLES = {
    draft:     { bg: "#F3F4F6", fg: "#525252" },
    pending:   { bg: "#FEF3C7", fg: "#B45309" },
    received:  { bg: "#DCFCE7", fg: "#15803D" },
    cancelled: { bg: "#FEE2E2", fg: "#DC2626" },
};

export default function Purchases() {
    const [purchases, setPurchases] = useState([]);
    const [products, setProducts] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ supplier_id: "", items: [], discount: 0, status: "received", notes: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [pu, pr, su] = await Promise.all([
                api.get("/purchases"),
                api.get("/products", { params: { limit: 500 } }),
                api.get("/suppliers"),
            ]);
            setPurchases(pu.data);
            setProducts(pr.data);
            setSuppliers(su.data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setForm({ supplier_id: "", items: [], discount: 0, status: "received", notes: "" });
        setDialogOpen(true);
    };

    const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", quantity: 1, unit_price: 0, gst_percent: 18 }] }));
    const updateItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
    const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

    const onProductSelect = (idx, productId) => {
        const p = products.find((x) => x.id === productId);
        updateItem(idx, {
            product_id: productId,
            unit_price: p ? Number(p.purchase_price || 0) : 0,
            gst_percent: p ? Number(p.gst_percent || 0) : 18,
        });
    };

    const totals = useMemo(() => {
        let subtotal = 0, tax = 0;
        for (const it of form.items) {
            const line = Number(it.quantity || 0) * Number(it.unit_price || 0);
            subtotal += line;
            tax += line * Number(it.gst_percent || 0) / 100;
        }
        return { subtotal, tax, total: subtotal + tax - Number(form.discount || 0) };
    }, [form.items, form.discount]);

    const submit = async () => {
        if (form.items.length === 0) return toast.error("Add at least one item");
        if (form.items.some((it) => !it.product_id)) return toast.error("Select product for all items");
        setSaving(true);
        try {
            await api.post("/purchases", {
                supplier_id: form.supplier_id || null,
                items: form.items.map((it) => ({
                    product_id: it.product_id,
                    quantity: Number(it.quantity || 0),
                    unit_price: Number(it.unit_price || 0),
                    gst_percent: Number(it.gst_percent || 0),
                })),
                discount: Number(form.discount || 0),
                status: form.status,
                notes: form.notes,
            });
            toast.success("Purchase recorded");
            setDialogOpen(false);
            load();
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSaving(false); }
    };

    const changeStatus = async (purchase, newStatus) => {
        try {
            await api.patch(`/purchases/${purchase.id}/status`, { status: newStatus });
            toast.success(`Purchase marked ${newStatus}`);
            load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    const exportCsv = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export?kind=purchases`, "_blank");

    return (
        <div className="space-y-6" data-testid="purchases-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Procurement</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Purchases</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">{purchases.length} purchase{purchases.length !== 1 && "s"} recorded.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={exportCsv} data-testid="purchases-export-btn"><DownloadSimple size={14} className="mr-1.5" /> CSV</Button>
                    <Button onClick={openCreate} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="purchases-add-btn">
                        <Plus size={16} weight="bold" className="mr-1.5" /> New Purchase
                    </Button>
                </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="purchases-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                            <tr>
                                <th className="text-left px-5 py-3 font-semibold">Purchase</th>
                                <th className="text-left px-5 py-3 font-semibold">Supplier</th>
                                <th className="text-left px-5 py-3 font-semibold">Items</th>
                                <th className="text-right px-5 py-3 font-semibold">Total</th>
                                <th className="text-left px-5 py-3 font-semibold">Status</th>
                                <th className="text-left px-5 py-3 font-semibold">Date</th>
                                <th className="text-right px-5 py-3 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={7} className="text-center py-10 text-neutral-400">Loading...</td></tr>}
                            {!loading && purchases.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-10 text-neutral-400">No purchases yet.</td></tr>
                            )}
                            {purchases.map((p) => (
                                <tr key={p.id} className="data-row border-t border-neutral-100" data-testid={`purchase-row-${p.purchase_no}`}>
                                    <td className="px-5 py-3 font-mono text-xs font-semibold">{p.purchase_no}</td>
                                    <td className="px-5 py-3">{p.supplier_name}</td>
                                    <td className="px-5 py-3 text-xs text-neutral-500">{p.items.length} item{p.items.length !== 1 && "s"}</td>
                                    <td className="px-5 py-3 text-right font-bold tabular font-display">{formatCurrency(p.total)}</td>
                                    <td className="px-5 py-3"><StatusPill label={p.status} styleMap={STATUS_STYLES} /></td>
                                    <td className="px-5 py-3 text-xs text-neutral-500 whitespace-nowrap">{formatDate(p.received_at || p.created_at)}</td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        {p.status !== "received" && p.status !== "cancelled" && (
                                            <button onClick={() => changeStatus(p, "received")} className="text-xs font-semibold text-green-700 hover:text-green-800 mr-3"
                                                    data-testid={`purchase-receive-${p.purchase_no}`}>Mark Received</button>
                                        )}
                                        {p.status !== "cancelled" && (
                                            <button onClick={() => changeStatus(p, "cancelled")} className="text-xs font-semibold text-red-600 hover:text-red-800"
                                                    data-testid={`purchase-cancel-${p.purchase_no}`}>Cancel</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="purchase-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold font-display">New Purchase</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Supplier</Label>
                            <Select value={form.supplier_id || "direct"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "direct" ? "" : v })}>
                                <SelectTrigger className="mt-1.5" data-testid="purchase-form-supplier"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="direct">Direct Purchase</SelectItem>
                                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.company}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="purchase-form-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">Draft</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="received">Received (adds stock)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2 mt-2">
                            <div className="eyebrow">Line items</div>
                            <Button variant="outline" size="sm" onClick={addItem} data-testid="purchase-form-add-item">
                                <Plus size={14} className="mr-1" /> Add item
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {form.items.length === 0 && (
                                <div className="text-xs text-neutral-500 border border-dashed border-neutral-300 rounded p-4 text-center">No items yet — add one to continue.</div>
                            )}
                            {form.items.map((it, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-neutral-50 rounded p-2" data-testid={`purchase-item-${idx}`}>
                                    <div className="col-span-12 md:col-span-5">
                                        <Select value={it.product_id} onValueChange={(v) => onProductSelect(idx, v)}>
                                            <SelectTrigger className="h-9"><SelectValue placeholder="Select product" /></SelectTrigger>
                                            <SelectContent>
                                                {products.map((pp) => <SelectItem key={pp.id} value={pp.id}>{pp.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Input type="number" placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className="col-span-4 md:col-span-2 h-9" />
                                    <Input type="number" placeholder="Cost" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} className="col-span-4 md:col-span-2 h-9" />
                                    <Input type="number" placeholder="GST%" value={it.gst_percent} onChange={(e) => updateItem(idx, { gst_percent: e.target.value })} className="col-span-3 md:col-span-2 h-9" />
                                    <button onClick={() => removeItem(idx)} className="col-span-1 text-neutral-500 hover:text-red-600 flex items-center justify-center" data-testid={`purchase-item-remove-${idx}`}>
                                        <X size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Discount (₹)</Label>
                            <Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className="mt-1.5" data-testid="purchase-form-discount" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Notes</Label>
                            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={1} className="mt-1.5" data-testid="purchase-form-notes" />
                        </div>
                    </div>

                    <div className="bg-neutral-50 rounded p-4 mt-2">
                        <div className="flex justify-between text-sm py-1"><span className="text-neutral-500">Subtotal</span><span className="tabular font-semibold">{formatCurrency(totals.subtotal)}</span></div>
                        <div className="flex justify-between text-sm py-1"><span className="text-neutral-500">GST</span><span className="tabular font-semibold">{formatCurrency(totals.tax)}</span></div>
                        <div className="flex justify-between text-sm py-1"><span className="text-neutral-500">Discount</span><span className="tabular font-semibold">-{formatCurrency(form.discount)}</span></div>
                        <div className="flex justify-between text-lg py-2 mt-2 border-t border-neutral-200"><span className="font-bold font-display">Total</span><span className="tabular font-extrabold font-display" style={{ color: "#15803D" }}>{formatCurrency(totals.total)}</span></div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="purchase-form-cancel">Cancel</Button>
                        <Button onClick={submit} disabled={saving} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="purchase-form-submit">
                            {saving ? "Saving..." : "Record Purchase"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatusPill({ label, styleMap }) {
    const s = styleMap[label] || { bg: "#F3F4F6", fg: "#374151" };
    return (
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: s.bg, color: s.fg }}>{label}</span>
    );
}
